import { z } from 'zod';

/* ---------- apuvälineet ---------- */

const emptyToNull = (v: unknown) => (v === '' || v === undefined ? null : v);

/** Valinnainen kokonaisluku lomakkeelta: '' → null */
export const optionalCount = z.preprocess(
  (v) => {
    const e = emptyToNull(v);
    return e === null ? null : Number(e);
  },
  z.number({ error: 'Anna numero' }).int('Anna kokonaisluku').min(0, 'Ei voi olla negatiivinen').max(1000).nullable(),
);

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Päivämäärä muodossa VVVV-KK-PP');
export const optionalDate = z.preprocess(emptyToNull, isoDate.nullable());

export const callSign = z
  .string()
  .trim()
  .min(3, 'Kutsu on liian lyhyt')
  .max(20, 'Kutsu on liian pitkä')
  .regex(/^[A-Za-z0-9/]+$/, 'Kutsussa saa olla vain kirjaimia, numeroita ja kauttaviivoja');

export const genre = z.enum(['OM', 'YL']);
export const userClass = z.enum(['Yleisluokka', 'Perusluokka']);

const password = z.string().min(6, 'Salasanan on oltava vähintään 6 merkkiä').max(200);

/* ---------- kirjautuminen ja rekisteröityminen ---------- */

export const loginSchema = z.object({
  username: z.string().trim().min(1, 'Anna kutsu'),
  password: z.string().min(1, 'Anna salasana'),
});
export type LoginInput = z.infer<typeof loginSchema>;

const profileFields = {
  name: z.string().trim().min(1, 'Nimi on pakollinen').max(100),
  address1: z.string().trim().max(100).default(''),
  address2: z.string().trim().max(100).default(''),
  postcode: z.string().trim().max(10).default(''),
  city: z.string().trim().max(50).default(''),
  email: z.email('Tarkista sähköpostiosoite').max(100),
  licenceDate: isoDate,
  genre,
  userClass,
};

export const registerSchema = z
  .object({
    id: callSign.refine((v) => v === v.toUpperCase(), 'Kirjoita kutsu kokonaan isoilla kirjaimilla'),
    ...profileFields,
    address1: z.string().trim().min(1, 'Osoite on pakollinen').max(100),
    postcode: z.string().trim().min(1, 'Postinumero on pakollinen').max(10),
    city: z.string().trim().min(1, 'Kaupunki on pakollinen').max(50),
    password,
    passwordConfirm: z.string(),
  })
  .refine((d) => d.password === d.passwordConfirm, { message: 'Salasanat eivät täsmää', path: ['passwordConfirm'] });
export type RegisterInput = z.infer<typeof registerSchema>;

const passwordChange = {
  password: z.preprocess(emptyToNull, password.nullable()).optional(),
  passwordConfirm: z.preprocess(emptyToNull, z.string().nullable()).optional(),
};

export const userUpdateSchema = z
  .object({
    ...profileFields,
    roleId: z.coerce.number().int().min(1).max(2).optional(),
    active: z.boolean().optional(),
    ...passwordChange,
  })
  .refine((d) => !d.password || d.password === d.passwordConfirm, {
    message: 'Salasanat eivät täsmää',
    path: ['passwordConfirm'],
  });
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

export const userCreateSchema = z
  .object({
    id: callSign,
    ...profileFields,
    roleId: z.coerce.number().int().min(1).max(2).default(2),
    active: z.boolean().default(true),
    ...passwordChange,
  })
  .refine((d) => !d.password || d.password === d.passwordConfirm, {
    message: 'Salasanat eivät täsmää',
    path: ['passwordConfirm'],
  });
export type UserCreateInput = z.infer<typeof userCreateSchema>;

/* ---------- kiinteät arvot ---------- */

export const ohcwSchema = z.object({
  noOfCountries: optionalCount,
  noOfCountriesTotal: optionalCount,
  noOfCountriesDate: optionalDate,
  hideFromDivisionReport: z.boolean(),
  cwNoOfCountries: optionalCount,
  cwNoOfCountriesTotal: optionalCount,
  cwNoOfCountriesDate: optionalDate,
  hideFromCwReport: z.boolean(),
});
export type OhcwInput = z.infer<typeof ohcwSchema>;

export const bandModeFixedSchema = z.object({
  values: z
    .array(z.object({ band: z.number().positive(), mode: z.string().min(1).max(10), value: optionalCount }))
    .max(500),
  digiAll: optionalCount,
});
export type BandModeFixedInput = z.infer<typeof bandModeFixedSchema>;

export const marathonSchema = z.object({
  values: z.array(z.object({ band: z.number().positive(), value: optionalCount })).max(100),
});
export type MarathonInput = z.infer<typeof marathonSchema>;

/* ---------- QSO:t ---------- */

export const qsoInputSchema = z.object({
  callSign: z
    .string()
    .trim()
    .min(2, 'Anna kutsu')
    .max(20)
    .transform((v) => v.toUpperCase()),
  date: isoDate,
  time: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Aika muodossa TT:MM')
    .transform((v) => (v.length === 5 ? `${v}:00` : v)),
  band: z.coerce.number({ error: 'Valitse band' }).positive('Valitse band'),
  mode: z.string().trim().min(1, 'Valitse moodi').max(10).transform((v) => v.toUpperCase()),
  countryId: z.coerce.number({ error: 'Valitse maa' }).int().positive('Valitse maa'),
  frequency: z.preprocess((v) => {
    const e = emptyToNull(v);
    return typeof e === 'string' ? Number(e.replace(',', '.')) : e;
  }, z.number().nonnegative().max(300000).nullable().optional()),
});
export type QsoInput = z.output<typeof qsoInputSchema>;
export type QsoFormValues = z.input<typeof qsoInputSchema>;

export const qsoBatchSchema = z.object({
  rows: z.array(qsoInputSchema).min(1).max(2000),
});

export const qsoKeySchema = z.object({ ids: z.array(z.number().int().positive()).min(1).max(5000) });

export const qsoUpdateSchema = z.object({
  rows: z.array(qsoInputSchema.extend({ id: z.number().int().positive() })).min(1).max(500),
});

/* ---------- raportit ---------- */

export const groupColumn = z.enum(['band', 'mode', 'parent_mode']);

export const reportFiltersSchema = z
  .object({
    bands: z.array(z.number().positive()).max(50).default([]),
    modes: z.array(z.string().max(10)).max(50).default([]),
    countries: z.array(z.number().int().positive()).max(500).default([]),
    genre: genre.nullable().default(null),
    callSign: z.string().trim().max(20).default(''),
    licenceMinYears: z.number().int().min(0).max(100).nullable().default(null),
    licenceMaxYears: z.number().int().min(1).max(100).nullable().default(null),
    groupBy: z.array(groupColumn).max(2).default([]),
  })
  .refine(
    (f) => f.licenceMinYears === null || f.licenceMaxYears === null || f.licenceMaxYears > f.licenceMinYears,
    { message: 'Ylärajan on oltava suurempi kuin alaraja', path: ['licenceMaxYears'] },
  );
export type ReportFilters = z.output<typeof reportFiltersSchema>;

export const reportSaveSchema = z.object({
  name: z.string().trim().min(1, 'Anna raportille nimi').max(100),
  heading: z.string().trim().max(500).default(''),
  filters: reportFiltersSchema,
});
export type ReportSaveInput = z.input<typeof reportSaveSchema>;

export const REPORT_KINDS = ['divisions', 'cw', 'marathon', 'band_modes', 'custom'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/* ---------- kilpailut ---------- */

export const competitionRulesSchema = z.object({
  licenceMinYears: z.number().int().min(0).max(100).nullable().default(null),
  licenceMaxYears: z.number().int().min(1).max(200).nullable().default(null),
  bands: z.array(z.number().positive()).default([]),
  modes: z.array(z.string().max(10)).default([]),
  countries: z.array(z.number().int().positive()).default([]),
  genres: z.array(genre).default([]),
  classes: z.array(userClass).default([]),
});
export type CompetitionRules = z.output<typeof competitionRulesSchema>;

export const competitionSchema = z
  .object({
    heading: z.string().trim().min(1, 'Otsikko on pakollinen').max(200),
    description: z.string().trim().min(1, 'Kuvaus on pakollinen').max(5000),
    startDate: isoDate,
    endDate: isoDate,
    rules: competitionRulesSchema,
  })
  .refine((c) => c.endDate >= c.startDate, { message: 'Loppupäivä ei voi olla ennen alkua', path: ['endDate'] });
export type CompetitionInput = z.input<typeof competitionSchema>;

/* ---------- ylläpito ---------- */

export const countrySchema = z.object({
  name: z.string().trim().min(1, 'Nimi on pakollinen').max(100),
  code: z.string().trim().min(1, 'Prefiksi on pakollinen').max(6).transform((v) => v.toUpperCase()),
  dxcc: z.string().trim().max(4).regex(/^\d*$/, 'DXCC-numero').default(''),
  deleted: z.boolean().default(false),
});
export type CountryInput = z.input<typeof countrySchema>;

export const countryCodesSchema = z.object({
  codes: z.string().max(20000).default(''),
  from: z.string().trim().max(4).default(''),
  to: z.string().trim().max(4).default(''),
});
export type CountryCodesInput = z.input<typeof countryCodesSchema>;

export const bandSchema = z.object({
  band: z.preprocess((v) => (typeof v === 'string' ? Number(v.replace(',', '.')) : v), z.number().positive().max(10000)),
});

export const modeSchema = z.object({
  mode: z.string().trim().min(1).max(10).regex(/^[A-Za-z0-9-]+$/, 'Vain kirjaimia ja numeroita').transform((v) => v.toUpperCase()),
  parentMode: z.preprocess(emptyToNull, z.string().max(10).nullable()),
});

export const helpSchema = z.object({ text: z.string().max(20000) });

export const settingsSchema = z.object({
  settings: z.array(z.object({ name: z.string().trim().min(1).max(100), value: z.string().max(5000) })).max(200),
});
